import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { CatButton } from "blocks-nextjs/blocks-react/components/Button/Button";
import { CatCheckboxField } from "blocks-nextjs/blocks-react/components/CheckboxField/CheckboxField";
import { CatDatepickerField } from "blocks-nextjs/blocks-react/components/DatepickerField/DatepickerField";
import { CatHeading } from "blocks-nextjs/blocks-react/components/Heading/Heading";
import { CatIconCalendarClock } from "blocks-nextjs/blocks-react/components/Icons/CalendarClock/CalendarClock";
import { CatIconClose } from "blocks-nextjs/blocks-react/components/Icons/Close/Close";
import { CatIconShow } from "blocks-nextjs/blocks-react/components/Icons/Show/Show";
import { CatIconSyncronize } from "blocks-nextjs/blocks-react/components/Icons/Syncronize/Syncronize";
import { CatInputField } from "blocks-nextjs/blocks-react/components/InputField/InputField";
import React, { useEffect, useRef, useState } from "react";
import Frame from "../../assets/Frame 627345.svg";
import { useDatePickerLiveViewConstraints } from "../../hooks/useDatePickerLiveViewConstraints";
import { usePatchChargerLink } from "../../services/query/eflow-charge-operation-planner-api.query";
import useGlobalStore from "../../store/useGlobalStore";
import useProjectDetailsStore from "../../store/useProjectDetailsStore";
import { ChargerAssetLinkUpdateRequest } from "../../swagger/eFlowService-v1";
import { formatExpiryDate, getMinDateForLiveView } from "../../utils/dateFormatter";
import { Variant } from "../GlobalNotification/GlobalNotification";
const EditLinkModal = ({ isOpen, linkData, onClose, onSave }) => {
    const [formData, setFormData] = useState({
        expiryDate: "",
        features: {
            canSeeHistory: false,
            canUseChargerControls: false,
            canUseSchedule: false,
            liveViewOnly: false
        },
        name: ""
    });
    const dateRef = useRef(null);
    useEffect(() => {
        if (linkData && isOpen) {
            // Convert date format if needed - the datepicker expects MM/dd/yyyy format
            let formattedDate = linkData.expiryDate;
            // If the date is in a different format, convert it
            if (linkData.expiryDate) {
                try {
                    // Try to parse the date
                    const dateObj = new Date(linkData.expiryDate);
                    if (!isNaN(dateObj.getTime())) {
                        // Format as MM/dd/yyyy
                        const month = String(dateObj.getMonth() + 1).padStart(2, "0");
                        const day = String(dateObj.getDate()).padStart(2, "0");
                        const year = dateObj.getFullYear();
                        formattedDate = `${month}/${day}/${year}`;
                    }
                }
                catch (error) {
                    console.error("Error formatting date:", error);
                }
            }
            setFormData({
                expiryDate: formattedDate,
                features: { ...linkData.features },
                name: linkData.name
            });
        }
    }, [linkData, isOpen]);
    // Apply Live View Only date constraints to the datepicker's internal calendar
    const isMaxTwoDays = formData.features.liveViewOnly;
    const isMaxThreeMonths = formData.features.canSeeHistory || formData.features.canUseChargerControls || formData.features.canUseSchedule;
    useDatePickerLiveViewConstraints(dateRef, isMaxTwoDays, isMaxThreeMonths, formData.expiryDate);
    const { mutate: patchChargerLinkMutate } = usePatchChargerLink();
    const { setGlobalNotification, userPartyNumber } = useGlobalStore((state) => ({
        setGlobalNotification: state.setGlobalNotification,
        userPartyNumber: state.userPartyNumber
    }));
    const { assetDetails, projectMetaData } = useProjectDetailsStore((state) => ({
        assetDetails: state.assetDetails,
        projectMetaData: state.projectMetaData
    }));
    // Get timezone offset from project metadata for timezone-aware date formatting
    const timezoneOffset = projectMetaData?.offset || "UTC+00:00";
    const handleInputChange = (field, value) => {
        // Validate name field: max 100 characters, only alphanumeric, spaces, @, and -
        if (field === "name") {
            // Remove any characters that are not alphanumeric, space, @, or -
            const sanitizedValue = value.replace(/[^a-zA-Z0-9\s@-]/g, "");
            // Limit to 100 characters
            const truncatedValue = sanitizedValue.slice(0, 100);
            setFormData((prev) => ({
                ...prev,
                [field]: truncatedValue
            }));
        }
        else if (field === "expiryDate") {
            // Validate expiry date when Live View Only is active
            setFormData((prev) => {
                // If Live View Only is checked, validate the date is within 2 days
                if (prev.features.liveViewOnly && value) {
                    try {
                        let selectedDate;
                        // Parse the date - handle both MM/dd/yyyy and ISO string formats
                        if (value.includes("/")) {
                            // MM/dd/yyyy format
                            const [month, day, year] = value.split("/").map(Number);
                            selectedDate = new Date(year, month - 1, day);
                        }
                        else {
                            // ISO string format
                            selectedDate = new Date(value);
                        }
                        // Normalize to start of day for accurate comparison
                        selectedDate.setHours(0, 0, 0, 0);
                        const today = new Date();
                        today.setHours(0, 0, 0, 0);
                        const maxDate = new Date();
                        maxDate.setDate(maxDate.getDate() + 1); // Today + next 1 day (2 days total: Today and Tomorrow)
                        maxDate.setHours(0, 0, 0, 0); // Start of day for comparison
                        // If selected date is beyond the allowed range, don't update
                        if (selectedDate < today || selectedDate > maxDate) {
                            console.warn("Date exceeds Live View Only allowed range (today and tomorrow only), ignoring change");
                            return prev; // Don't update state
                        }
                    }
                    catch (error) {
                        console.error("Error validating date:", error);
                    }
                }
                return {
                    ...prev,
                    [field]: value
                };
            });
        }
        else {
            setFormData((prev) => ({
                ...prev,
                [field]: value
            }));
        }
    };
    const handleFeatureChange = (feature, checked) => {
        setFormData((prev) => {
            const newFeatures = {
                ...prev.features,
                [feature]: checked
            };
            if (feature === "liveViewOnly" && checked) {
                newFeatures.canSeeHistory = false;
                newFeatures.canUseChargerControls = false;
                newFeatures.canUseSchedule = false;
                // Check if current expiry date exceeds the 2-day limit for Live View Only
                if (prev.expiryDate) {
                    try {
                        let selectedDate;
                        // Parse the date - handle both MM/dd/yyyy and ISO string formats
                        if (prev.expiryDate.includes("/")) {
                            // MM/dd/yyyy format
                            const [month, day, year] = prev.expiryDate.split("/").map(Number);
                            selectedDate = new Date(year, month - 1, day);
                        }
                        else {
                            // ISO string format
                            selectedDate = new Date(prev.expiryDate);
                        }
                        // Normalize to start of day for accurate comparison
                        selectedDate.setHours(0, 0, 0, 0);
                        const today = new Date();
                        today.setHours(0, 0, 0, 0);
                        const maxDate = new Date();
                        maxDate.setDate(maxDate.getDate() + 1); // Today + next 1 day (2 days total: Today and Tomorrow)
                        maxDate.setHours(0, 0, 0, 0); // Start of day for comparison
                        // If selected date is beyond the allowed range, clear it
                        if (selectedDate < today || selectedDate > maxDate) {
                            return {
                                expiryDate: "", // Clear the date
                                features: newFeatures,
                                name: prev.name
                            };
                        }
                    }
                    catch (error) {
                        console.error("Error validating date:", error);
                    }
                }
            }
            // When checking a non-Live View Only feature
            if (feature !== "liveViewOnly" && checked) {
                // Disable Live View Only if it was previously checked
                if (prev.features.liveViewOnly) {
                    newFeatures.liveViewOnly = false;
                }
            }
            return {
                ...prev,
                features: newFeatures
            };
        });
    };
    const handleSave = () => {
        if (linkData) {
            // Don't proceed if required IDs are missing
            if (!assetDetails?.assetId || !projectMetaData?.projectId || !userPartyNumber) {
                console.error("Cannot save: Missing required assetId, projectId, or partyNumber");
                return;
            }
            const updatedLink = {
                ...linkData,
                expiryDate: formData.expiryDate,
                features: formData.features,
                name: formData.name
            };
            const selectedFeatures = [];
            if (formData.features.liveViewOnly)
                selectedFeatures.push("live view only");
            if (formData.features.canSeeHistory)
                selectedFeatures.push("can see history");
            if (formData.features.canUseChargerControls)
                selectedFeatures.push("can use charger controls");
            if (formData.features.canUseSchedule)
                selectedFeatures.push("can use schedule");
            // Format expiry date to MM/dd/yyyy format with timezone offset
            const formattedExpiryDate = formatExpiryDate(formData.expiryDate, timezoneOffset);
            const chargerAssetLinkUpdateRequest = {
                assetId: assetDetails.assetId,
                expiryDate: formattedExpiryDate,
                features: [...selectedFeatures],
                link: updatedLink.link,
                name: updatedLink.name,
                partyNumber: userPartyNumber,
                projectId: projectMetaData.projectId,
                status: ChargerAssetLinkUpdateRequest.status.ACTIVE
            };
            // Call PATCH endpoint: /assets/chargers/links
            patchChargerLinkMutate(chargerAssetLinkUpdateRequest, {
                onError: () => {
                    const errorMessage = "Failed to update the link";
                    setGlobalNotification({
                        message: `${errorMessage}.`,
                        showNotification: true,
                        type: Variant.error
                    });
                    onClose();
                },
                onSuccess: () => {
                    onSave({ ...updatedLink, expiryDate: formattedExpiryDate });
                    onClose();
                }
            });
        }
    };
    if (!isOpen || !linkData)
        return null;
    const hasSelectedFeature = Object.values(formData.features).some((isEnabled) => isEnabled);
    const isFormValid = formData.name.trim() !== "" && formData.expiryDate.trim() !== "" && hasSelectedFeature;
    return (_jsx("div", { className: "fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50", children: _jsxs("div", { className: "bg-white rounded-lg shadow-xl w-full max-w-2xl mx-4 overflow-hidden", children: [_jsxs("div", { className: "flex justify-between items-center p-4", children: [_jsx(CatHeading, { variant: "title-sm", children: "Edit Link" }), _jsx("button", { className: "text-gray-500 hover:text-gray-700", "data-test": "edit-link-modal-close-button", onClick: onClose, children: _jsx(CatIconClose, {}) })] }), _jsxs("div", { className: "px-4 max-h-[80vh] overflow-y-auto", children: [_jsx("p", { className: "text-gray-600 mb-6 text-sm", children: "Allows users to generate a custom shareable link with a chosen name, expiration date, and specific feature permissions such as viewing history using charger controls, or setting schedules." }), _jsxs("div", { className: "flex flex-col sm:flex-row gap-4 sm:gap-6 mb-6", children: [_jsxs("div", { className: "flex-1", children: [_jsxs("label", { className: "block text-sm font-medium text-gray-700 mb-2", children: ["Name ", _jsx("span", { className: "text-grey-500", children: "(required)" })] }), _jsx("div", { className: "h-12", children: _jsx(CatInputField, { className: "h-full custom-height-input", "data-test": "edit-link-name-input", fieldId: "edit-link-name", onBlChange: (e) => handleInputChange("name", e.detail?.value || ""), placeholder: "Enter link name", size: "sm", value: formData.name }) }), _jsxs("div", { className: "!text-[10px] text-gray-600 mt-1", children: [formData.name.length, "/100 characters. Allowed: letters, numbers, spaces, @ and -"] })] }), _jsxs("div", { className: "flex-1", children: [_jsxs("label", { className: "block text-sm font-medium text-gray-700 mb-2", children: ["Expire date ", _jsx("span", { className: "text-grey-500", children: "(required)" })] }), _jsx("div", { className: "relative h-12", children: _jsx(CatDatepickerField, { ariaDescribedBy: "edit-link-datepicker", className: "h-full custom-height-input", "data-test": "edit-link-expire-date-picker", dateFormat: "MM/dd/yyyy", disabledMinDate: getMinDateForLiveView(), fieldId: "edit-link-datepicker", onBlDatePickerChange: (e) => handleInputChange("expiryDate", e.detail?.value || ""), placeholder: "mm/dd/yyyy", ref: dateRef, size: "sm", value: formData.expiryDate }) }), formData.features.liveViewOnly && (_jsx("div", { className: " !text-[10px] text-gray-600 mt-2", "data-test": "expire-date-hint", children: "For Live View Only, expiration date must be within today + next 1 day." }))] })] }), _jsxs("div", { children: [_jsxs("label", { className: "block text-sm font-medium text-gray-700 mb-4", children: ["Features ", _jsx("span", { className: "text-grey-500", children: "(required)" })] }), _jsxs("div", { className: "space-y-4", children: [_jsxs("div", { className: "flex items-start space-x-3", children: [_jsx(CatCheckboxField, { checked: formData.features.liveViewOnly, "data-test": "edit-link-live-view-only-checkbox", disabled: formData.features.canSeeHistory ||
                                                        formData.features.canUseChargerControls ||
                                                        formData.features.canUseSchedule, fieldId: "edit-live-view-only", onBlCheckboxChange: (e) => handleFeatureChange("liveViewOnly", e.detail?.checked ?? false) }), _jsxs("div", { className: "flex-1", children: [_jsxs("div", { className: "flex items-center space-x-2", children: [_jsx(CatIconShow, { className: "w-4 h-4" }), _jsx("span", { className: "font-medium", children: "Live View Only" })] }), _jsx("p", { className: "text-sm text-gray-500 mt-1", children: "Can be used without a CWS ID (disables others)" })] })] }), _jsxs("div", { className: "flex items-start space-x-3", children: [_jsx(CatCheckboxField, { checked: formData.features.canSeeHistory, "data-test": "edit-link-can-see-history-checkbox", disabled: formData.features.liveViewOnly, fieldId: "edit-can-see-history", onBlCheckboxChange: (e) => handleFeatureChange("canSeeHistory", e.detail?.checked ?? false) }), _jsxs("div", { className: "flex-1", children: [_jsxs("div", { className: "flex items-center space-x-2", children: [_jsx(CatIconSyncronize, { className: "w-4 h-4" }), _jsx("span", { className: `font-medium ${formData.features.liveViewOnly ? "text-gray-400" : ""}`, children: "Can see history" })] }), _jsx("p", { className: `text-sm mt-1 ${formData.features.liveViewOnly ? "text-gray-400" : "text-gray-500"}`, children: "Allows the user to view the history of charging sessions." })] })] }), _jsxs("div", { className: "flex items-start space-x-3", children: [_jsx(CatCheckboxField, { checked: formData.features.canUseChargerControls, "data-test": "edit-link-can-use-charger-controls-checkbox", disabled: formData.features.liveViewOnly, fieldId: "edit-can-use-charger-controls", onBlCheckboxChange: (e) => handleFeatureChange("canUseChargerControls", e.detail?.checked ?? false) }), _jsxs("div", { className: "flex-1", children: [_jsxs("div", { className: "flex items-center space-x-2", children: [_jsx("img", { alt: "charger controls", className: "w-4 h-4", src: Frame }), _jsx("span", { className: `font-medium ${formData.features.liveViewOnly ? "text-gray-400" : ""}`, children: "Can use charger controls" })] }), _jsx("p", { className: `text-sm mt-1 ${formData.features.liveViewOnly ? "text-gray-400" : "text-gray-500"}`, children: "Let's the user manage charging by starting or stopping the charger." })] })] }), _jsxs("div", { className: "flex items-start space-x-3", children: [_jsx(CatCheckboxField, { checked: formData.features.canUseSchedule, "data-test": "edit-link-can-use-schedule-checkbox", disabled: formData.features.liveViewOnly, fieldId: "edit-can-use-schedule", onBlCheckboxChange: (e) => handleFeatureChange("canUseSchedule", e.detail?.checked ?? false) }), _jsxs("div", { className: "flex-1", children: [_jsxs("div", { className: "flex items-center space-x-2", children: [_jsx(CatIconCalendarClock, { className: "w-4 h-4" }), _jsx("span", { className: `font-medium ${formData.features.liveViewOnly ? "text-gray-400" : ""}`, children: "Can use schedule" })] }), _jsx("p", { className: `text-sm mt-1 ${formData.features.liveViewOnly ? "text-gray-400" : "text-gray-500"}`, children: "Let's user plan ahead by setting charging times." })] })] })] })] })] }), _jsxs("div", { className: "p-6 bg-white-50 flex justify-start gap-4", children: [_jsx(CatButton, { className: "bg-yellow-400 text-black font-medium", "data-test": "edit-link-save-button", disabled: !isFormValid, onClick: handleSave, variant: "primary", children: "Save" }), _jsx("button", { className: "text-blue-600 font-medium hover:text-blue-800", "data-test": "edit-link-cancel-button", onClick: onClose, children: "Cancel" })] })] }) }));
};
export default EditLinkModal;
